/**
 * Vue i18n Plugin Configuration
 *
 * Loads translations from the backend database.
 * Supports 7 languages: ES, EN, CA, FR, DE, RU, ZH
 */

import { createI18n } from 'vue-i18n'
import type { I18n, Composer } from 'vue-i18n'
import { api } from '@/api/client'

// Available languages
export const LANGUAGES = ['es', 'en', 'ca', 'fr', 'de', 'ru', 'zh'] as const
export type Language = (typeof LANGUAGES)[number]

// Language display names
export const LANGUAGE_NAMES: Record<Language, string> = {
  es: 'Espanol',
  en: 'English',
  ca: 'Catala',
  fr: 'Francais',
  de: 'Deutsch',
  ru: 'Russkiy',
  zh: 'Zhongwen'
}

// Language flags (emoji)
export const LANGUAGE_FLAGS: Record<Language, string> = {
  es: '🇪🇸',
  en: '🇬🇧',
  ca: '🏴󠁥󠁳󠁣󠁴󠁿',
  fr: '🇫🇷',
  de: '🇩🇪',
  ru: '🇷🇺',
  zh: '🇨🇳'
}

// Country codes for flag images (ISO 3166-1 alpha-2)
// Using flagcdn.com - for Catalan we use Andorra (where Catalan is official)
export const LANGUAGE_COUNTRY_CODES: Record<Language, string> = {
  es: 'es',
  en: 'gb',
  ca: 'ad', // Andorra (Catalan is official language)
  fr: 'fr',
  de: 'de',
  ru: 'ru',
  zh: 'cn'
}

// Create i18n instance
export const i18n = createI18n({
  legacy: false,
  locale: 'es',
  fallbackLocale: 'es',
  messages: {},
  missingWarn: false,
  fallbackWarn: false
})

// Helper to get the composer (typed for Composition API mode)
function getComposer(): Composer {
  return i18n.global as unknown as Composer
}

// Track loaded locales to avoid reloading
const loadedLocales = new Set<string>()

/**
 * Load translation messages for a locale from the backend.
 */
export async function loadLocaleMessages(locale: Language): Promise<boolean> {
  const composer = getComposer()

  // Already loaded?
  if (loadedLocales.has(locale)) {
    composer.locale.value = locale
    return true
  }

  try {
    const response = await api.get(`/i18n/translations/${locale}`)

    if (response.data.success) {
      // Set messages for this locale
      composer.setLocaleMessage(locale, response.data.data)
      loadedLocales.add(locale)
      composer.locale.value = locale
      return true
    }
  } catch (error) {
    console.error(`Failed to load translations for ${locale}:`, error)
  }

  return false
}

/**
 * Set the current locale and persist to localStorage.
 */
export async function setLocale(locale: Language): Promise<boolean> {
  localStorage.setItem('locale', locale)
  document.documentElement.lang = locale

  return await loadLocaleMessages(locale)
}

/**
 * Get the initial locale from localStorage or browser settings.
 */
export function getInitialLocale(): Language {
  // Check localStorage first
  const saved = localStorage.getItem('locale') as Language | null
  if (saved && LANGUAGES.includes(saved)) {
    return saved
  }

  // Check browser language
  const browserLang = navigator.language.split('-')[0] as Language
  if (LANGUAGES.includes(browserLang)) {
    return browserLang
  }

  // Default to Spanish
  return 'es'
}

/**
 * Initialize i18n with the saved or default locale.
 */
export async function initI18n(): Promise<void> {
  const locale = getInitialLocale()
  await setLocale(locale)
}
